import { ReactNode, useEffect } from 'react';
import { createTheme, StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import ErrorBoundary from './ErrorBoundary';
import { setupErrorHandlers } from '@/shared/lib/utils';
import { ToastContainer } from 'react-toastify';
import { setUnauthorizedHandler } from '@/shared/api/http';
import useToken from '@/shared/hooks/useToken';

/**
 * Public routes that don't require authentication
 * Users on these routes should not be redirected on 401
 */
const PUBLIC_ROUTES = ['/sign-in', '/sign-up', '/forgot-password'];

// Setup enhanced error logging in development mode
setupErrorHandlers();

// Create a query client for TanStack Query
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000, // 5 minutes
        },
    },
});

const theme = createTheme({
    palette: {
        primary: { main: '#7875FF' },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '10px',
                    '&.MuiButton-contained.Mui-disabled': {
                        opacity: 0.75,
                        filter: 'grayscale(100%)',
                        color: 'white',
                    },
                },
            },
        },
        MuiButtonBase: {
            styleOverrides: {
                root: {
                    borderRadius: '10px !important',
                    '&.Mui-selected': {
                        '& .MuiTypography-root': {
                            fontWeight: 500,
                            fontFamily: 'inter',
                        },
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    borderRadius: '10px !important',
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: '14px !important',
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    margin: 0,
                    lineHeight: '0.75rem',
                    marginTop: '0.5rem',
                    color: 'rgb(239 68 68)',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    '&.MuiPaper-root.MuiPaper-elevation.MuiDrawer-paperAnchorLeft': {
                        backgroundColor: '#102132',
                        color: 'white',
                    },
                },
            },
        },
    },
    typography: {
        fontFamily: [
            'Inter',
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
        ].join(','),
    },
});

type AppProvidersProps = {
    children: ReactNode;
};

/**
 * Inner component to access navigate hook and queryClient
 * Sets up unauthorized handler for HTTP interceptor
 */
const AppProvidersInner = ({ children }: AppProvidersProps) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { setToken } = useToken();

    useEffect(() => {
        // Wire up unauthorized handler to clear state and navigate
        setUnauthorizedHandler(async () => {
            const pathname = window.location.pathname;
            
            // Check if already on a public route - do nothing if so
            const isPublicRoute = PUBLIC_ROUTES.some((route) => pathname.startsWith(route));
            if (isPublicRoute) {
                return;
            }

            // Clear token
            setToken(null);
            // Clear React Query cache
            queryClient.clear();
            // Navigate to sign-in page
            navigate('/sign-in', { replace: true });
        });
    }, [navigate, queryClient, setToken]);

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                {children}
                <ToastContainer />
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

export const AppProviders = ({ children }: AppProvidersProps) => {
    return (
        <ErrorBoundary>
            <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                    <AppProvidersInner>{children}</AppProvidersInner>
                </BrowserRouter>
            </QueryClientProvider>
        </ErrorBoundary>
    );
};
